import { Button, Collapse, DatePicker, Form, Input, message, Select } from "antd";
import { NamePath } from "antd/es/form/interface";
import TextArea from "antd/es/input/TextArea";
import { ApiService } from "api/api.service";
import { PopulatedBlog } from "api/blog.service";
import { BlogFeatured, BlogStatus, BlogVisibility } from "components/content-pages/BlogCustomFormItems";
import ImageGalleryModal from "components/content-pages/ImageGalleryModal";
import PageTitle from "components/PageTitle";
import { SingleQueryWrapper } from "components/QueryWrapper";
import RichTextEditor from "components/RichTextEditor";
import dayjs from "dayjs";
import { BlogDto, BlogSitesDto, BlogStatusTypes, BlogVisibilityTypes, TwitterCardTypes } from "dtos/blog.dto";
import { T_Category, T_Category_Type } from "dtos/category.dto";
import DashboardLayout from "layout/DashboardLayout";
import { TbChevronRight } from "react-icons/tb";
import { useNavigate, useParams } from "react-router-dom";
import { slugify } from "utils/functions";

export default function BlogItemNew() {
  const { blogSlug } = useParams();

  const blog = ApiService.blog.useGetByIdOrSlug(blogSlug);
  const categoryList = ApiService.category.useGetAllPaginated({ page: 1, limit: 1000, type: [T_Category_Type.BLOG] });

  return (
    <SingleQueryWrapper
      query={categoryList}
      renderData={(categoryListData) => {
        if (!blogSlug) return <BlogInternal categories={categoryListData?.data} />;
        return (
          <SingleQueryWrapper
            showLoaderOn="isFetching"
            query={blog}
            renderData={(blogData) => {
              return <BlogInternal blog={blogData} categories={categoryListData?.data} />;
            }}
          />
        );
      }}
    />
  );
}

function BlogInternal({ blog, categories }: { blog?: PopulatedBlog; categories: T_Category[] }) {
  const isEditMode = !!blog;
  const [form] = Form.useForm<BlogDto>();
  const navigate = useNavigate();
  const { data: blogSites } = ApiService.blog.useGetBlogSites();

  function isNotModified(fieldName: NamePath<BlogDto>) {
    return form.isFieldTouched(fieldName) === false || form.getFieldValue(fieldName)?.length === 0;
  }
  const createBlog = ApiService.blog.useCreateBlog();

  const updateBlog = ApiService.blog.useUpdateByIdOrSlug();

  return (
    <Form
      form={form}
      layout="vertical"
      requiredMark="optional"
      preserve={false}
      initialValues={
        isEditMode
          ? {
              ...blog,
              published_at: blog?.published_at ? dayjs(blog?.published_at) : undefined,
              categories: blog?.categories?.map((cat) => cat._id),
              blog_sites: blog.blog_sites?.map((site) => site._id),
            }
          : {
              status: BlogStatusTypes.DRAFT,
              visiblity: BlogVisibilityTypes.OPEN_TO_ALL,
              featured: false,
              og: {
                ogSiteName: "<site_name>",
              },
              twitter: {
                twitterSite: "<twitter_site_name>",
                twitterAuthor: "<twitter_author>",
                twitterCardType: TwitterCardTypes.SUMMARY_LARGE_IMAGE,
              },
            }
      }
      onFinish={(values) => {
        if (isEditMode) {
          updateBlog.mutate(
            {
              id: blog._id,
              body: {
                ...values,
                published_at: (values?.published_at as any)?.toISOString(),
              },
            },
            {
              onSuccess: () => {
                message.success("Blog Updated successfully");
                form.resetFields();
                navigate(`/dashboard/blogs?page=${1}&limit=${10}`);
              },
            },
          );
        } else {
          createBlog.mutate(
            {
              ...values,
              published_at: (values?.published_at as any)?.toISOString(),
              og: {
                ...values?.og,
                ogUrl: `https://firstchoicedebtsolutions.org/blogs/${values?.slug}`,
              },
            },
            {
              onSuccess: () => {
                message.success("Blog created successfully");
                form.resetFields();
                navigate(`/dashboard/blogs?page=${1}&limit=${10}`);
              },
            },
          );
        }
      }}
      onFieldsChange={(changedFields) => {
        if (isEditMode) return;
        const fieldName = changedFields?.[0]?.name?.[0];
        const fieldValue = changedFields?.[0]?.value;

        if (fieldName === "title") {
          if (isNotModified("slug")) {
            form.setFieldValue("slug", `${slugify(fieldValue as string)}`);
          }
          if (isNotModified("pagetitle")) {
            form.setFieldValue("pagetitle", fieldValue);
          }
          if (isNotModified(["og", "seoPageTitle"])) {
            form.setFieldValue(["og", "seoPageTitle"], `${fieldValue} | <site_title>`);
          }
          if (isNotModified(["og", "ogTitle"])) {
            form.setFieldValue(["og", "ogTitle"], `${fieldValue} | <site_title>`);
          }
          if (isNotModified(["twitter", "twitterTitle"])) {
            form.setFieldValue(["twitter", "twitterTitle"], `${fieldValue} | <twitter_title>`);
          }
        } else if (fieldName === "description") {
          if (isNotModified(["og", "seoDescription"])) {
            form.setFieldValue(["og", "seoDescription"], `${fieldValue} | <site_description>`);
          }
          if (isNotModified(["og", "ogDescription"])) {
            form.setFieldValue(["og", "ogDescription"], `${fieldValue} | <site_description>`);
          }
          if (isNotModified(["twitter", "twitterDescription"])) {
            form.setFieldValue(["twitter", "twitterDescription"], `${fieldValue} | <twitter_description>`);
          }
        } else if (fieldName === "feature_image") {
          if (isNotModified(["og", "ogImage"])) {
            form.setFieldValue(["og", "ogImage"], fieldValue);
          }
        }
      }}
    >
      <DashboardLayout
        headerRight={
          <div className="flex gap-3">
            <Form.Item<BlogDto> name={"status"} className="!m-0">
              <BlogStatus />
            </Form.Item>
            <Form.Item<BlogDto> name={"visiblity"} className="!m-0">
              <BlogVisibility />
            </Form.Item>
            <Form.Item<BlogDto> name={"featured"} className="!m-0">
              <BlogFeatured />
            </Form.Item>
            <Button
              className="ml-3"
              color="primary"
              variant="solid"
              onClick={() => form.submit()}
              loading={isEditMode ? updateBlog.isPending : createBlog.isPending}
            >
              {isEditMode ? "Save Changes" : "Create Blog"}
            </Button>
          </div>
        }
      >
        <PageTitle
          title={isEditMode ? "Edit Blog" : "Add Blog"}
          pageTitle={[{ title: "Blogs", link: "/dashboard/blogs" }, { title: isEditMode ? "Edit Blog" : "Add Blog" }]}
        />
        <div className="grid grid-cols-1 lg:grid-cols-[auto_450px]">
          <div className="h-[calc(100vh-50px)] overflow-y-auto">
            <Form.Item<BlogDto> name={"title"} rules={[{ required: true, message: "Give blog a title" }]}>
              <Input.TextArea
                className="text-4xl font-extrabold h-fit"
                autoSize={{ minRows: 1, maxRows: 3 }}
                placeholder="Blog Title"
                variant="borderless"
              />
            </Form.Item>
            <Form.Item<BlogDto> name={"html"} rules={[{ required: true, message: "Write blog content" }]}>
              <RichTextEditor />
            </Form.Item>
          </div>
          <div className="h-[calc(100vh-50px)] overflow-y-auto border-0 border-l border-solid border-gray-200">
            <div className="flex flex-col p-4">
              <Form.Item<BlogDto> name="feature_image">
                <ImageGalleryModal />
              </Form.Item>
              <Form.Item<BlogDto> label="Description" name="description" rules={[{ required: true }]}>
                <TextArea />
              </Form.Item>
              <Form.Item<BlogDto> label="Slug" name="slug" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item<BlogDto> label="Published At" name="published_at" rules={[{ required: true }]}>
                <DatePicker />
              </Form.Item>
              <Form.Item<BlogDto> label="Page Title" name="pagetitle" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item<BlogDto> label="Keywords" name="tags" rules={[{ required: true }]}>
                <Select mode="tags" />
              </Form.Item>
              <Form.Item<BlogDto> label="Category" name="categories" rules={[{ required: true }]}>
                <Select
                  mode="multiple"
                  allowClear
                  options={categories.map((cat: T_Category) => {
                    return { label: cat.title, value: cat._id };
                  })}
                />
              </Form.Item>
              <Form.Item<BlogDto> label="Blog Sites" name="blog_sites" rules={[{ required: true }]}>
                <Select
                  mode="multiple"
                  allowClear
                  options={
                    blogSites
                      ? blogSites.map((site: BlogSitesDto) => {
                          return { label: site.name, value: site._id };
                        })
                      : []
                  }
                />
              </Form.Item>
            </div>
            <Collapse
              expandIcon={({ isActive }) => (
                <TbChevronRight
                  className={`transition-transform`}
                  style={{
                    transform: isActive ? "rotate(90deg)" : "rotate(0deg)",
                  }}
                />
              )}
              style={{ borderRadius: 0, borderLeft: 0, borderRight: 0 }}
              items={[
                {
                  label: "SEO",
                  children: (
                    <>
                      <Form.Item<BlogDto> label="SEO Page Title" name={["og", "seoPageTitle"]}>
                        <Input />
                      </Form.Item>
                      <Form.Item<BlogDto> label="SEO Description" name={["og", "seoDescription"]}>
                        <TextArea />
                      </Form.Item>
                    </>
                  ),
                },
                {
                  label: "Open Graph (Facebook, WhatsApp, Instagram)",
                  children: (
                    <>
                      <Form.Item<BlogDto> label="OG Title" name={["og", "ogTitle"]}>
                        <Input />
                      </Form.Item>
                      <Form.Item<BlogDto> label="OG Description" name={["og", "ogDescription"]}>
                        <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} />
                      </Form.Item>
                      {/* <Form.Item<BlogDto> label="OG URL" name={["og", "ogUrl"]}>
                      <Input />
                    </Form.Item> */}
                      <Form.Item<BlogDto> label="OG Image" name={["og", "ogImage"]}>
                        <ImageGalleryModal />
                        {/* <Input placeholder="Enter image URL or leave blank for thumbnail" /> */}
                      </Form.Item>
                      <Form.Item<BlogDto> label="OG Site Name" name={["og", "ogSiteName"]}>
                        <Input defaultValue="<site_name>" />
                      </Form.Item>
                    </>
                  ),
                },
                {
                  label: "Twitter",
                  children: (
                    <>
                      <Form.Item<BlogDto> label="Card Type" name={["twitter", "twitterCardType"]}>
                        <Select
                          placeholder="Select card type"
                          options={[
                            { label: "Summary", value: TwitterCardTypes.SUMMARY },
                            { label: "Summary Large Image", value: TwitterCardTypes.SUMMARY_LARGE_IMAGE },
                          ]}
                        />
                      </Form.Item>
                      <Form.Item<BlogDto> label="Twitter Title" name={["twitter", "twitterTitle"]}>
                        <Input />
                      </Form.Item>
                      <Form.Item<BlogDto> label="Twitter Description" name={["twitter", "twitterDescription"]}>
                        <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} />
                      </Form.Item>
                      <Form.Item<BlogDto> label="Twitter Site" name={["twitter", "twitterSite"]}>
                        <Input placeholder="e.g. @YourSiteHandle" defaultValue={"<twitter_site_name>"} />
                      </Form.Item>
                      <Form.Item<BlogDto> label="Twitter Author" name={["twitter", "twitterAuthor"]}>
                        <Input placeholder="e.g. @AuthorHandle" defaultValue={"<twitter_author>"} />
                      </Form.Item>
                    </>
                  ),
                },
              ]}
            />
          </div>
        </div>
      </DashboardLayout>
    </Form>
  );
}
